const DB_NAME = "shubhfin_offline";
const DB_VERSION = 1;
const STORE_NAME = "offline_queue";

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function queueOfflineRequest(url, method, headers, body, meta = {}) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  const item = {
    id: `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    url,
    method,
    headers: { ...headers },
    body: typeof body === "string" ? body : JSON.stringify(body),
    timestamp: new Date().toISOString(),
    meta,
  };
  store.add(item);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = reject;
  });
  db.close();

  // Request background sync if available
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    const reg = await navigator.serviceWorker.ready;
    try {
      await reg.sync.register("sync-collections");
    } catch (e) {
      // Background sync not supported in this browser
    }
  }

  return item.id;
}

export async function getPendingQueue() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const items = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return items;
  } catch {
    return [];
  }
}

export async function getPendingCount() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const count = await new Promise((resolve, reject) => {
      const req = store.count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return count;
  } catch {
    return 0;
  }
}

export async function removeFromQueue(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  tx.objectStore(STORE_NAME).delete(id);
  await new Promise((resolve) => {
    tx.oncomplete = resolve;
  });
  db.close();
}

export async function syncPendingRequests() {
  const items = await getPendingQueue();
  const results = { synced: 0, failed: 0, errors: [] };

  for (const item of items) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: item.headers,
        body: item.body,
      });
      if (response.ok) {
        await removeFromQueue(item.id);
        results.synced++;
      } else {
        const errData = await response.json().catch(() => ({}));
        results.failed++;
        results.errors.push({ id: item.id, error: errData.detail || `HTTP ${response.status}` });
      }
    } catch (e) {
      results.failed++;
      results.errors.push({ id: item.id, error: "Network error" });
    }
  }

  return results;
}

export async function clearQueue() {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  tx.objectStore(STORE_NAME).clear();
  await new Promise((resolve) => {
    tx.oncomplete = resolve;
  });
  db.close();
}
